import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { DialogProps } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { UserProfileDefaultFields, useUserProfileFieldsQuery } from 'api';
import {
  BaseTextFieldController,
  CountryCodeSelect,
  DatePicker,
  Form,
  FormGroup,
  SelectController,
  FullPageProgress,
  Spinner,
  NewConfirmDialog,
} from 'components';
import { countries, FIELDS_VALUE_LENGTH } from 'constants/index';
import { useWindowSize } from 'hooks';
import { textFieldMobileStyles, textFieldStyles } from 'styles';
import { UserProfileField, UserExtendedDetails } from 'types';
import { stripMultipleSpaces, stripNonDigits } from 'utils';
import { required } from 'validations';

import { CLOTHES_SIZE_OPTIONS, EDIT_FORM_FIELDS_LENGTH, RELATION_OPTIONS } from './constants';
import { useEditEmployeeForm } from './hooks';
import { phoneRules, relationPhoneRules, ssnRules } from './rules';
import {
  CancelBtn,
  CreateBtn,
  DialogTitle,
  Footer,
  FormButtonsWrapper,
  GroupTitle,
  ModalContent,
  ModalOverlay,
  PhoneInputW,
  SpinnerW,
} from './styled';
import {
  countryCodeInputStyles,
  textAreaFieldStyles,
  countryCodeInputMobileStyles,
} from './stylesConfig';

interface EditEmployeeFormProps extends DialogProps {
  onClose: () => void;
  employee: UserExtendedDetails;
  refetchUserDetails?: () => void;
  formTitle: string;
  mode?: 'edit' | 'activate';
}

export function EditEmployeeForm({
  onClose,
  employee,
  refetchUserDetails,
  mode = 'edit',
  formTitle,
}: EditEmployeeFormProps): ReactElement {
  const { t } = useTranslation();
  const {
    control,
    handleFormSubmit,
    isLoading,
    isOpenConfirmModal,
    handleTitleClick,
    handleCloseForm,
    handleCloseConfirmModal,
  } = useEditEmployeeForm({ employee, onClose, refetchUserDetails, mode });
  const { isLoading: isUserProfileOrderListLoading, userProfileFields = [] } =
    useUserProfileFieldsQuery({});

  const { isMobile } = useWindowSize();
  const formGroupMarginBottom = isMobile ? 8 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  const renderFields = (field: UserProfileField) => {
    switch (field.label) {
      case UserProfileDefaultFields.NAME:
        return (
          <>
            <FormGroup marginBottom={formGroupMarginBottom} row>
              <BaseTextFieldController
                name="firstName"
                control={control}
                placeholder={t('profile.edit.form.publicInfo.placeholders.firstName')}
                sx={{
                  width: '100%',
                  ...fieldStyles,
                }}
                maxLength={FIELDS_VALUE_LENGTH.firstName.max}
                format={stripMultipleSpaces}
                preventSpaceOnStart
                rules={required}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom} row>
              <BaseTextFieldController
                name="lastName"
                control={control}
                placeholder={t('profile.edit.form.publicInfo.placeholders.lastName')}
                sx={{
                  width: '100%',
                  ...fieldStyles,
                }}
                maxLength={FIELDS_VALUE_LENGTH.lastName.max}
                format={stripMultipleSpaces}
                preventSpaceOnStart
                rules={required}
              />
            </FormGroup>
          </>
        );
      case UserProfileDefaultFields.EMAIL:
        return (
          <FormGroup marginBottom={formGroupMarginBottom} row>
            <BaseTextFieldController
              name="email"
              control={control}
              disabled
              placeholder={t('profile.edit.form.publicInfo.placeholders.email')}
              sx={{ width: '100%', ...fieldStyles, opacity: '0.5' }}
              maxLength={FIELDS_VALUE_LENGTH.email.max}
              rules={required}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.PHONE:
        return (
          <FormGroup
            marginBottom={isMobile ? 12 : 24}
            row
            sx={{ columnGap: isMobile ? '8px' : '16px' }}
          >
            <CountryCodeSelect
              control={control}
              name="countryCode"
              containerStyle={{
                width: isMobile ? '105px' : '170px',
                height: isMobile ? '54px' : '58px',
              }}
              inputStyle={isMobile ? countryCodeInputMobileStyles : countryCodeInputStyles}
              buttonStyle={{
                left: '10px',
              }}
            />
            <PhoneInputW>
              <BaseTextFieldController
                name="phone"
                control={control}
                placeholder={t('profile.edit.form.publicInfo.placeholders.phone')}
                sx={fieldStyles}
                format={stripNonDigits}
                maxLength={FIELDS_VALUE_LENGTH.phone.max}
                rules={phoneRules}
              />
            </PhoneInputW>
          </FormGroup>
        );
      case UserProfileDefaultFields.BIRTHDATE:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <DatePicker
              name="birthDate"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.birthDate')}
              sx={fieldStyles}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.DEPARTMENT:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="department"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.companyDep')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.companyDepartment.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.POSITION:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="position"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.companyPos')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.position.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.ADDRESS1:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="address1"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.address1')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.address1.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.ADDRESS2:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="address2"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.address2')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.address2.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.CITY:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="city"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.city')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.city.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.REGION:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="region"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.state')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.province.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.POSTCODE:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="postCode"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.postcode')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.postCode.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.COUNTRY:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <SelectController
              name="country"
              control={control}
              placeholder={t('profile.edit.form.publicInfo.placeholders.country')}
              options={countries}
              withDefaultEmptyOption
              sx={{ borderRadius: '8px' }}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.SSN:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="ssn"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.ssn')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.ssn.max}
              rules={ssnRules}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.EMPLOYMENTDATE:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <DatePicker
              name="employmentDate"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.employmentDate')}
              sx={fieldStyles}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.EMPLOYMENTNUMBER:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="employmentNumber"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.employmentNumber')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.employmentNumber.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.EDUCATION:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="education"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.education')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.education.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.SIZEOFCLOTHES:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <SelectController
              name="clothesSize"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.sizeOfClothes')}
              options={CLOTHES_SIZE_OPTIONS}
              withDefaultEmptyOption
              sx={{ borderRadius: '8px' }}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.ALLERGIES:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="allergies"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.allergies')}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.allergies.max}
            />
          </FormGroup>
        );
      case UserProfileDefaultFields.COMMENT:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name="comments"
              control={control}
              placeholder={t('profile.edit.form.privateInfo.placeholders.comment')}
              sx={textAreaFieldStyles}
              multiline
              maxLength={EDIT_FORM_FIELDS_LENGTH.comments.max}
            />
          </FormGroup>
        );
      default:
        return (
          <FormGroup marginBottom={formGroupMarginBottom}>
            <BaseTextFieldController
              name={`custom_profile_fields.${field.label}`}
              control={control}
              placeholder={field.label}
              sx={fieldStyles}
              maxLength={EDIT_FORM_FIELDS_LENGTH.companyDepartment.max}
            />
          </FormGroup>
        );
    }
  };

  if (isUserProfileOrderListLoading) {
    return (
      <SpinnerW>
        <Spinner size="1.5rem" wrapperSx={{ height: '24px' }} />
      </SpinnerW>
    );
  }

  return (
    <>
      <ModalOverlay>
        {isMobile && (
          <DialogTitle onClick={handleTitleClick}>
            <KeyboardBackspaceRoundedIcon fontSize="large" />
            {formTitle}
          </DialogTitle>
        )}
        <ModalContent>
          {!isMobile && (
            <DialogTitle onClick={handleTitleClick}>
              <KeyboardBackspaceRoundedIcon fontSize="large" />
              {formTitle}
            </DialogTitle>
          )}

          <Form onSubmit={handleFormSubmit}>
            <GroupTitle>{t('profile.edit.form.publicInfo.title')}</GroupTitle>
            {userProfileFields
              .filter((field) => field.category === 'public')
              .map((field) => renderFields(field))}

            <GroupTitle sx={{ marginTop: isMobile ? '8px' : 0 }}>
              {t('profile.edit.form.privateInfo.title')}
            </GroupTitle>

            {userProfileFields
              .filter((field) => field.category === 'private')
              .map((field) => renderFields(field))}

            <GroupTitle sx={{ marginTop: isMobile ? '8px' : 0 }}>
              {t('profile.edit.form.emergency.title')}
            </GroupTitle>

            <FormGroup marginBottom={formGroupMarginBottom}>
              <SelectController
                name="relation"
                control={control}
                placeholder={t('profile.edit.form.emergency.placeholders.relation')}
                options={RELATION_OPTIONS}
                withDefaultEmptyOption
                sx={{ borderRadius: '8px' }}
              />
            </FormGroup>
            <FormGroup
              marginBottom={formGroupMarginBottom}
              row
              sx={{ flexDirection: isMobile ? 'column' : 'row' }}
            >
              <BaseTextFieldController
                name="relationContactFirstName"
                control={control}
                placeholder={t('profile.edit.form.publicInfo.placeholders.firstName')}
                sx={{
                  width: '100%',
                  ...fieldStyles,
                }}
                maxLength={FIELDS_VALUE_LENGTH.firstName.max}
                format={stripMultipleSpaces}
                preventSpaceOnStart
              />
            </FormGroup>
            <FormGroup
              marginBottom={formGroupMarginBottom}
              row
              sx={{ flexDirection: isMobile ? 'column' : 'row' }}
            >
              <BaseTextFieldController
                name="relationContactLastName"
                control={control}
                placeholder={t('profile.edit.form.publicInfo.placeholders.lastName')}
                sx={{
                  width: '100%',
                  ...fieldStyles,
                }}
                maxLength={FIELDS_VALUE_LENGTH.lastName.max}
                format={stripMultipleSpaces}
                preventSpaceOnStart
              />
            </FormGroup>
            <FormGroup
              marginBottom={isMobile ? 12 : 24}
              row
              sx={{ columnGap: isMobile ? '8px' : '16px' }}
            >
              <CountryCodeSelect
                control={control}
                name="relationCountryCode"
                containerStyle={{
                  width: isMobile ? '105px' : '170px',
                  height: isMobile ? '54px' : '58px',
                }}
                inputStyle={isMobile ? countryCodeInputMobileStyles : countryCodeInputStyles}
                buttonStyle={{
                  left: '10px',
                }}
              />
              <PhoneInputW>
                <BaseTextFieldController
                  name="relationContactPhone"
                  control={control}
                  placeholder={t('profile.edit.form.publicInfo.placeholders.phone')}
                  sx={fieldStyles}
                  format={stripNonDigits}
                  maxLength={FIELDS_VALUE_LENGTH.phone.max}
                  rules={relationPhoneRules}
                />
              </PhoneInputW>
            </FormGroup>

            <Footer>
              <FormButtonsWrapper>
                <CancelBtn variant="outlined" onClick={onClose}>
                  {t('document.create.form.buttons.cancel')}
                </CancelBtn>
                <CreateBtn variant="contained" onClick={handleFormSubmit} disabled={isLoading}>
                  {t('document.create.form.buttons.edit')}
                </CreateBtn>
              </FormButtonsWrapper>
            </Footer>
          </Form>
        </ModalContent>
      </ModalOverlay>

      <NewConfirmDialog
        title={t('profile.edit.modal.title')}
        subTitle={t('profile.edit.modal.exit')}
        open={isOpenConfirmModal}
        onConfirm={handleCloseForm}
        onClose={handleCloseConfirmModal}
        submitLabel={t('profile.edit.modal.successButton')}
        cancelLabel={t('profile.edit.modal.cancelButton')}
      />

      {isLoading && <FullPageProgress />}
    </>
  );
}
